class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  connectedCallback() {
    this.hoverMedia = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    this.hoverTimers = new Map();
    this.hoverEvents = new AbortController();
    const options = { signal: this.hoverEvents.signal };
    this.querySelectorAll('details').forEach((details) => {
      const hoverTarget = details === this.mainDetailsToggle
        ? this
        : details.closest('.g24-nav-dropdown-child') || details;
      hoverTarget.addEventListener('pointerenter', (event) => {
        if (!this.hoverMedia.matches || event.pointerType !== 'mouse') return;
        this.cancelHoverTimer(details);
        this.hoverTimers.set(details, setTimeout(() => {
          this.hoverTimers.delete(details);
          if (!this.hoverMedia.matches || !this.isConnected) return;
          const navigation = this.closest('.header__inline-menu');
          const peers = details === this.mainDetailsToggle
            ? navigation.querySelectorAll('header-menu > details')
            : details.parentElement.parentElement.querySelectorAll(':scope > li > details');
          peers.forEach((peer) => {
            if (peer !== details) peer.closest('header-menu').closeBranch(peer);
          });
          details.open = true;
          details.querySelector(':scope > summary').setAttribute('aria-expanded', 'true');
        }, 100));
      }, options);
      hoverTarget.addEventListener('pointerleave', (event) => {
        if (!this.hoverMedia.matches || event.pointerType !== 'mouse') return;
        this.cancelHoverTimer(details);
        this.hoverTimers.set(details, setTimeout(() => {
          this.hoverTimers.delete(details);
          if (details.contains(document.activeElement) && this.keyboardNavigation) return;
          this.closeBranch(details);
        }, 220));
      }, options);
      details.addEventListener('toggle', () => {
        details.querySelector(':scope > summary').setAttribute('aria-expanded', String(details.open));
        if (!details.open) {
          this.cancelHoverTimer(details);
          details.querySelectorAll('details').forEach((child) => this.closeBranch(child));
        }
      }, options);
    });
    this.addEventListener('keydown', () => { this.keyboardNavigation = true; }, options);
    this.addEventListener('pointerdown', () => { this.keyboardNavigation = false; }, options);
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.close();
    }, options);
    document.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape' || !this.mainDetailsToggle.open) return;
      const focusInside = this.contains(document.activeElement);
      this.close();
      if (focusInside) this.mainDetailsToggle.querySelector('summary').focus({ preventScroll: true });
    }, options);
    this.hoverMedia.addEventListener('change', () => {
      if (!this.hoverMedia.matches) this.close();
    }, options);
  }

  disconnectedCallback() {
    this.hoverEvents?.abort();
    this.hoverTimers?.forEach((timer) => clearTimeout(timer));
    this.hoverTimers?.clear();
  }

  cancelHoverTimer(details) {
    clearTimeout(this.hoverTimers?.get(details));
    this.hoverTimers?.delete(details);
  }

  closeBranch(details) {
    this.cancelHoverTimer(details);
    const summary = details.querySelector(':scope > summary');
    if (details.open && details.contains(document.activeElement) && document.activeElement !== summary) {
      summary.focus({ preventScroll: true });
    }
    details.querySelectorAll('details').forEach((child) => {
      this.cancelHoverTimer(child);
      child.open = false;
      child.querySelector(':scope > summary').setAttribute('aria-expanded', 'false');
    });
    details.open = false;
    summary.setAttribute('aria-expanded', 'false');
  }

  close() {
    this.closeBranch(this.mainDetailsToggle);
  }

  onToggle() {
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', String(this.mainDetailsToggle.open));
    if (!this.header) return;
    this.header.preventHide = Boolean(this.header.querySelector('.header__inline-menu details[open]'));
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
